import { useState } from 'react';
import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Download,
  Loader2,
  ShieldAlert,
  Wrench,
  X,
} from 'lucide-react';
import AuthenticatedDownloadLink from '../AuthenticatedDownloadLink';
import type { ToolCallState } from './types';

interface ToolCallCardProps {
  toolCall: ToolCallState;
  onDecision: (toolCall: ToolCallState, approve: boolean) => void;
  deciding: boolean;
}

function statusMeta(tc: ToolCallState) {
  switch (tc.status) {
    case 'running':
      return {
        icon: Loader2,
        spin: true,
        color: 'text-zt-info',
        badge: 'border-zt-info/30 bg-zt-info/10 text-zt-info',
        label: 'Running',
      };
    case 'done':
      return {
        icon: Check,
        spin: false,
        color: 'text-zt-primary',
        badge: 'border-zt-primary/30 bg-zt-primary/10 text-zt-primary',
        label: 'Completed',
      };
    case 'approval_required':
      return {
        icon: ShieldAlert,
        spin: false,
        color: 'text-zt-warning',
        badge: 'border-zt-warning/30 bg-zt-warning/10 text-zt-warning',
        label: 'Needs approval',
      };
    default:
      return {
        icon: CircleAlert,
        spin: false,
        color: 'text-zt-danger',
        badge: 'border-zt-danger/30 bg-zt-danger/10 text-zt-danger',
        label: 'Error',
      };
  }
}

function pretty(value: unknown): string {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function asRecord(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return value as Record<string, unknown>;
  }
  return null;
}

function extractDocumentHighlight(result: unknown): { title: string; downloadUrl: string } | null {
  const root = asRecord(result);
  if (!root) return null;
  const doc =
    asRecord(root.document) ||
    asRecord(asRecord(root.data)?.document) ||
    (typeof root.downloadUrl === 'string' ? root : null);
  if (!doc) return null;
  const downloadUrl =
    (typeof doc.downloadUrl === 'string' && doc.downloadUrl) ||
    (typeof doc.download_url === 'string' && doc.download_url) ||
    '';
  if (!downloadUrl) return null;
  const title =
    (typeof doc.title === 'string' && doc.title) ||
    (typeof doc.filename === 'string' && doc.filename) ||
    'Generated PDF';
  return { title, downloadUrl };
}

function extractSandboxHighlight(result: unknown): {
  ok: boolean;
  resultText: string;
  stdout: string;
  error: string;
  durationMs?: number;
} | null {
  const root = asRecord(result);
  if (!root) return null;
  const payload = asRecord(root.execution) || asRecord(root.data) || root;
  const hasSandboxShape =
    'stdout' in payload ||
    'result' in payload ||
    'timedOut' in payload ||
    'backend' in payload ||
    'durationMs' in payload;
  if (!hasSandboxShape) return null;
  const resultVal = payload.result;
  return {
    ok: Boolean(payload.ok ?? root.ok),
    resultText: resultVal === undefined ? '' : pretty(resultVal),
    stdout: typeof payload.stdout === 'string' ? payload.stdout : '',
    error:
      (typeof payload.error === 'string' && payload.error) ||
      (typeof payload.stderr === 'string' && payload.stderr) ||
      '',
    durationMs: typeof payload.durationMs === 'number' ? payload.durationMs : undefined,
  };
}

function extractFileMoveHighlight(result: unknown): {
  action: string;
  source: string;
  destination: string;
  contentType?: string;
  referenceId?: string;
  filename?: string;
  ok: boolean;
} | null {
  const root = asRecord(result);
  if (!root) return null;
  const payload = asRecord(root.data) || root;
  const action =
    (typeof payload.action === 'string' && payload.action) ||
    (typeof root.action === 'string' && root.action) ||
    '';
  const destination =
    (typeof payload.destination === 'string' && payload.destination) ||
    (typeof root.destination === 'string' && root.destination) ||
    '';
  const source =
    (typeof payload.source === 'string' && payload.source) ||
    (typeof root.source === 'string' && root.source) ||
    '';
  const looksLikeMove =
    Boolean(destination) &&
    (action === 'move' ||
      action === 'copy' ||
      'referenceId' in payload ||
      'reference_id' in payload ||
      ('source' in payload && 'destination' in payload));
  if (!looksLikeMove) return null;
  return {
    action: action || 'moved',
    source,
    destination,
    contentType:
      (typeof payload.contentType === 'string' && payload.contentType) ||
      (typeof payload.content_type === 'string' && payload.content_type) ||
      undefined,
    referenceId:
      (typeof payload.referenceId === 'string' && payload.referenceId) ||
      (typeof payload.reference_id === 'string' && payload.reference_id) ||
      undefined,
    filename: typeof payload.filename === 'string' ? payload.filename : undefined,
    ok: Boolean(payload.ok ?? root.ok ?? true),
  };
}

function ToolCallCard({ toolCall, onDecision, deciding }: ToolCallCardProps) {
  const [open, setOpen] = useState(toolCall.status === 'approval_required');
  const meta = statusMeta(toolCall);
  const Icon = meta.icon;
  const argsText = pretty(toolCall.arguments);
  const resultText = pretty(toolCall.result);
  const decided = toolCall.approvalStatus === 'approved' || toolCall.approvalStatus === 'rejected';
  const showApproval = toolCall.status === 'approval_required' && !!toolCall.approvalId;
  const docHighlight =
    toolCall.status === 'done' ? extractDocumentHighlight(toolCall.result) : null;
  const sandboxHighlight =
    toolCall.status === 'done' && !docHighlight ? extractSandboxHighlight(toolCall.result) : null;
  const fileMoveHighlight =
    toolCall.status === 'done' && !docHighlight && !sandboxHighlight
      ? extractFileMoveHighlight(toolCall.result)
      : null;
  const hasSpecialHighlight = Boolean(docHighlight || sandboxHighlight || fileMoveHighlight);

  return (
    <div className="overflow-hidden rounded-md border border-zt-border bg-zt-elevated/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-2.5 px-3 py-2 text-left transition hover:bg-zt-elevated/80"
      >
        {open ? (
          <ChevronDown size={14} className="shrink-0 text-zt-muted" />
        ) : (
          <ChevronRight size={14} className="shrink-0 text-zt-muted" />
        )}
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded border border-zt-border bg-zt-bg">
          <Wrench size={12} className="text-zt-muted" />
        </span>
        <span className="min-w-0 flex-1 truncate font-mono text-[12px] font-medium tracking-tight text-slate-200">
          {toolCall.name}
        </span>
        <span
          className={`inline-flex shrink-0 items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide ${meta.badge}`}
        >
          <Icon size={11} className={meta.spin ? 'animate-spin' : ''} />
          {meta.label}
        </span>
      </button>

      {open && (
        <div className="space-y-2.5 border-t border-zt-border bg-zt-bg/50 px-3 py-2.5">
          {argsText && (
            <div>
              <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zt-muted">
                Arguments
              </div>
              <pre className="overflow-x-auto rounded border border-zt-border/60 bg-zt-bg p-2 font-mono text-[11px] leading-relaxed text-slate-300">
                {argsText}
              </pre>
            </div>
          )}
          {toolCall.status === 'running' && (
            <div className="flex items-center gap-2 text-xs text-zt-muted">
              <Loader2 size={13} className="animate-spin text-zt-info" /> Awaiting tool result…
            </div>
          )}

          {docHighlight && (
            <div className="rounded-md border border-zt-primary/25 bg-zt-primary/5 px-3 py-2">
              <div className="text-xs font-medium text-slate-200">{docHighlight.title}</div>
              <AuthenticatedDownloadLink
                href={docHighlight.downloadUrl}
                forceAuthenticated
                filenameHint={
                  docHighlight.title.toLowerCase().endsWith('.pdf')
                    ? docHighlight.title
                    : `${docHighlight.title}.pdf`
                }
                className="mt-1 inline-flex items-center gap-1 text-[11px] text-zt-accent hover:underline"
              >
                <Download size={12} /> Download PDF
              </AuthenticatedDownloadLink>
            </div>
          )}

          {sandboxHighlight && (
            <div className="rounded-md border border-zt-border bg-zt-bg px-3 py-2 text-xs">
              <div className={sandboxHighlight.ok ? 'text-zt-primary' : 'text-zt-danger'}>
                {sandboxHighlight.ok ? 'Sandbox ok' : 'Sandbox error'}
                {sandboxHighlight.durationMs != null ? ` · ${sandboxHighlight.durationMs} ms` : ''}
              </div>
              {sandboxHighlight.resultText && (
                <pre className="mt-1 max-h-32 overflow-auto font-mono text-[12px] text-slate-300">
                  {sandboxHighlight.resultText}
                </pre>
              )}
              {sandboxHighlight.stdout && (
                <pre className="mt-1 max-h-24 overflow-auto font-mono text-[11px] text-zt-muted">
                  {sandboxHighlight.stdout}
                </pre>
              )}
              {sandboxHighlight.error && (
                <pre className="mt-1 max-h-24 overflow-auto font-mono text-[11px] text-zt-danger">
                  {sandboxHighlight.error}
                </pre>
              )}
            </div>
          )}

          {fileMoveHighlight && (
            <div className="rounded-md border border-zt-primary/25 bg-zt-primary/5 px-3 py-2 text-xs">
              <div className={fileMoveHighlight.ok ? 'text-zt-primary' : 'text-zt-danger'}>
                {fileMoveHighlight.ok ? fileMoveHighlight.action : `${fileMoveHighlight.action} failed`}
                {fileMoveHighlight.filename ? ` · ${fileMoveHighlight.filename}` : ''}
              </div>
              {fileMoveHighlight.referenceId && (
                <div className="mt-0.5 font-mono text-[11px] text-zt-muted">
                  ref {fileMoveHighlight.referenceId}
                </div>
              )}
              {fileMoveHighlight.source && (
                <div className="mt-1 text-[11px] text-slate-400">
                  from <span className="font-mono text-slate-300">{fileMoveHighlight.source}</span>
                </div>
              )}
              <div className="mt-0.5 text-[11px] text-slate-400">
                to <span className="font-mono text-slate-200">{fileMoveHighlight.destination}</span>
              </div>
              {fileMoveHighlight.contentType && (
                <div className="mt-0.5 text-[10px] text-zt-muted">{fileMoveHighlight.contentType}</div>
              )}
            </div>
          )}

          {resultText && !hasSpecialHighlight && (
            <div>
              <div className="mb-1 text-[10px] font-medium uppercase tracking-wider text-zt-muted">
                Result
              </div>
              <pre className="max-h-56 overflow-auto rounded border border-zt-border/60 bg-zt-bg p-2 font-mono text-[11px] leading-relaxed text-slate-300">
                {resultText}
              </pre>
            </div>
          )}
          {resultText && hasSpecialHighlight && (
            <details className="text-[11px] text-zt-muted">
              <summary className="cursor-pointer hover:text-slate-300">Raw result</summary>
              <pre className="mt-1 max-h-40 overflow-auto rounded border border-zt-border/60 bg-zt-bg p-2 font-mono text-[11px] text-slate-400">
                {resultText}
              </pre>
            </details>
          )}

          {showApproval && (
            <div className="rounded-md border border-zt-warning/30 bg-zt-warning/5 p-3">
              <div className="flex items-center gap-2 text-xs text-zt-warning">
                <ShieldAlert size={14} />
                Human approval required
                {toolCall.riskLevel && (
                  <span className="rounded border border-zt-warning/20 bg-zt-warning/10 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide">
                    {toolCall.riskLevel}
                  </span>
                )}
              </div>
              {decided ? (
                <div className="mt-2 text-xs text-zt-muted">
                  Approval {toolCall.approvalStatus}.
                </div>
              ) : (
                <div className="mt-2 flex items-center gap-2">
                  <button
                    type="button"
                    disabled={deciding}
                    onClick={() => onDecision(toolCall, true)}
                    className="flex items-center gap-1 rounded-md bg-zt-primary px-3 py-1.5 text-xs font-medium text-zt-bg hover:bg-zt-accent disabled:opacity-50"
                  >
                    <Check size={14} /> Approve &amp; run
                  </button>
                  <button
                    type="button"
                    disabled={deciding}
                    onClick={() => onDecision(toolCall, false)}
                    className="flex items-center gap-1 rounded-md border border-zt-border bg-zt-bg px-3 py-1.5 text-xs font-medium text-zt-danger hover:border-zt-danger/40 disabled:opacity-50"
                  >
                    <X size={14} /> Reject
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ToolCallCard;
